import React, { FC } from 'react';

import { useForm } from 'react-hook-form';
import * as O from 'fp-ts/Option';
import { Link } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { HttpError, HttpStatusCode } from '@core/http';
import { AuthenticateParams, authenticateSchema } from '@modules/auth/model';
import { renderOptional } from '@shared/utils/render';
import { Alert, Anchor, Button, Group, Text, TextInput, Title } from '@mantine/core';

interface LoginFormProps {
  loading: boolean;
  error: O.Option<HttpError>;
  onSubmit: (params: AuthenticateParams) => void;
}

const LoginForm: FC<LoginFormProps> = ({ loading, error, onSubmit }) => {
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<AuthenticateParams>({
    mode: 'onChange',
    resolver: zodResolver(authenticateSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });
  const mapErrorToMessage = (error: HttpError) => {
    switch (error.status) {
      case HttpStatusCode.UNAUTHORIZED:
        return 'Email ou mot de passe incorrect.';
      case HttpStatusCode.FORBIDDEN:
        return 'Votre compte n’est pas encore valide.';
      default:
        return 'Une erreur technique est survenue.';
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
      {renderOptional(error, error => (
        <Alert color="red" mb={10}>
          <Text color="red" size="sm" weight={600} align="center">
            {mapErrorToMessage(error)}
          </Text>
        </Alert>
      ))}

      <Title size="h3" mb="md">
        Connexion
      </Title>

      <TextInput
        id="email"
        type="email"
        label="Adresse e-mail"
        required
        placeholder="Adresse e-mail"
        error={!!errors.email}
        {...register('email')}
      />

      <TextInput
        mt="lg"
        type="password"
        id="password"
        label="Mot de passe"
        required
        placeholder="Mot de passe"
        error={!!errors.password}
        {...register('password')}
      />

      <Group mt={45} position="center">
        <Button type="submit" loading={loading} disabled={loading} radius={32} size="lg">
          Se connecter
        </Button>
      </Group>

      <Group mt={15} position="center">
        <Anchor
          component={Link}
          to="/password-reset/forgot"
          size="sm"
          weight="600"
          color="dark"
          sx={{ textDecoration: 'underline' }}
        >
          Mot de passe oublié ?
        </Anchor>
      </Group>
    </form>
  );
};

export default LoginForm;
